import {
  CircleCheck,
  CircleHelp,
  CirclePause,
  CircleX,
  GitCompareArrows,
  LucideIcon,
  RefreshCw,
} from 'lucide-react';

import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';

import {
  WorkflowStatus,
  WorkflowType,
  DeploymentPlatform,
} from '../workflows/types';

const BADGE_TYPE = {
  healthy: 'success',
  error: 'danger',
  // Blue rather than amber, so amber belongs to drift alone. Sharing it left
  // the two indistinguishable, and they call for different responses: syncing
  // resolves itself, drift does not.
  syncing: 'info',
  drifted: 'warn',
  paused: 'muted',
  unknown: 'muted',
} as const;

const STATUS_ICONS: Record<WorkflowStatus, LucideIcon> = {
  healthy: CircleCheck,
  error: CircleX,
  syncing: RefreshCw,
  // The same icon the drift detail uses, so the two read as one idea.
  drifted: GitCompareArrows,
  paused: CirclePause,
  unknown: CircleHelp,
};

const STATUS_LABELS: Record<WorkflowStatus, string> = {
  healthy: 'Healthy',
  error: 'Error',
  syncing: 'Syncing',
  drifted: 'Drifted',
  paused: 'Paused',
  unknown: 'Unknown',
};

export function StatusBadge({ status }: { status: WorkflowStatus }) {
  return (
    <Badge type={BADGE_TYPE[status]} shape="pill" size="md">
      <Icon icon={STATUS_ICONS[status]} size="xs" /> {STATUS_LABELS[status]}
    </Badge>
  );
}

const TYPE_LABELS: Record<WorkflowType, string> = {
  stack: 'Stack',
  edgeStack: 'Edge Stack',
  gitopsDeployment: 'Reconciled',
};
export function TypeBadge({ type }: { type: WorkflowType }) {
  return <Badge type="muted">{TYPE_LABELS[type]}</Badge>;
}

const PLATFORM_LABELS: Record<DeploymentPlatform, string> = {
  dockerStandalone: 'Docker Standalone',
  dockerSwarm: 'Docker Swarm',
  kubernetes: 'Kubernetes',
};
export function PlatformBadge({
  platform,
}: {
  platform: DeploymentPlatform | undefined;
}) {
  return (
    <Badge type="muted">
      {platform ? PLATFORM_LABELS[platform] : 'Unknown'}
    </Badge>
  );
}
